// Shared (client + server) rendering of Spotlight activity rows into human-readable text.
//
// Activity is persisted structurally — a machine `type` plus a `metadata` bag of params — and never
// as pre-rendered prose (see the SpotlightActivity table). Both the #server-spotlights Slack post
// (notifySpotlightActivity) and the admin sync modal call formatSpotlightActivity so the two always
// read identically, and improving the copy here reformats historical rows everywhere at once.
//
// A type absent from ACTIVITY_COPY (e.g. an older row written before its copy landed) falls back to
// metadata.action / metadata.details when present, else the raw type — so no row renders blank.

const { getGroupPlatformSpec, SPOTLIGHT_BRAND_FEATURE_FLAGS } = require('./spotlights');

const SPOTLIGHT_ACTIVITY_TYPES = {
  AUTHORIZATION_GRANTED: 'authorization:granted',
  AUTHORIZATION_REVOKED: 'authorization:revoked',
  AUTH_REQUEST_SENT: 'auth_request:sent',
  AUTH_REQUEST_ACCEPTED: 'auth_request:accepted',
  AUTH_REQUEST_REJECTED: 'auth_request:rejected',
  LAUNCH_BLOCKED: 'launch:blocked',
  LAUNCH_ATTEMPTED: 'launch:attempted',
  LAUNCH_AUTO_AFTER_APPROVAL: 'launch:auto_after_approval',
  SPOTLIGHT_PAUSED: 'spotlight:paused',
  // Recommendation-driven auto-pause (ENG-3866): the engine paused it because window ROAS fell below
  // the brand's pause guide. Actor 'system'. Deliberately NOT in PAUSE_ACTIVITY_TYPES so the Meta-drift
  // auto-unpause reconciliation never lifts it — it stays paused until the brand resumes.
  SPOTLIGHT_AUTO_PAUSED: 'spotlight:auto_paused',
  // Automation/reconciliation pauses — actor 'system'. Kept distinct from SPOTLIGHT_PAUSED so the
  // timeline (and the auto-unpause reconciliation) can tell an explicit brand/creator pause apart
  // from one the sync job applied.
  SPOTLIGHT_AUTO_PAUSED_TO_MATCH_META: 'spotlight:auto_paused_to_match_meta',
  META_REPAUSED_TO_MATCH_SHOPMY: 'meta:repaused_to_match_shopmy',
  META_OFFPHASE_AD_REPAUSED: 'meta:offphase_ad_repaused',
  SPOTLIGHT_RESUMED: 'spotlight:resumed',
  SPOTLIGHT_VALIDATED: 'spotlight:validated',
  SPOTLIGHT_DEMOTED: 'spotlight:demoted',
  SPOTLIGHT_CLOSED: 'spotlight:closed',
  DETAILS_UPDATED: 'details:updated',
  BUDGET_UPDATED: 'budget:updated',
  AUDIENCE_UPDATED: 'audience:updated',
  SETTINGS_UPDATED: 'settings:updated',
  EXCLUSIONS_UPDATED: 'exclusions:updated',
  EXCLUSIONS_ADDED: 'exclusions:added',
  EXCLUSIONS_SET: 'exclusions:set',
  META_SYNCED: 'meta:synced',
  META_CREATIVE_REBUILT: 'meta:creative_rebuilt',
  IDENTITY_REVERIFY_REGRESSED: 'identity:reverify_regressed',
  META_AD_RECONNECTED: 'meta:ad_reconnected',
  META_AD_RELAUNCHED: 'meta:ad_relaunched',
  META_AD_DISCONNECTED: 'meta:ad_disconnected',
  META_AD_PAUSED: 'meta:ad_paused',
  META_ADSET_DISCONNECTED: 'meta:adset_disconnected',
  META_PIXEL_CONNECTED: 'meta:pixel_connected',
  MEDIA_TRIM_FAILED: 'media:trim_failed',
  TRIAGE_MARKED_ERRORED: 'triage:marked_errored',
  TRIAGE_RESOLVED: 'triage:resolved',
  KILLSWITCH_DISABLED: 'killswitch:disabled',
  KILLSWITCH_ENABLED: 'killswitch:enabled',
  // Unpaid-ad-spend pause and its release. Actor 'system'. In PAUSE_ACTIVITY_TYPES (below) but never
  // an explicit pause, so the timeline attributes it to billing rather than to the brand.
  BILLING_AUTO_PAUSED: 'billing:auto_paused',
  BILLING_AUTO_RESUMED: 'billing:auto_resumed',
  // Admin released the billing pause without waiting for the invoice to settle.
  BILLING_ADMIN_RESUMED: 'billing:admin_resumed',
  // The brand's own off switch ("Pause all Spotlights" or a confirmed $0 daily budget) and its
  // explicit Resume. The brand-level pair carries the counts; the per-spotlight pair is what the
  // spotlight's timeline shows. The per-spotlight pause counts as an explicit brand pause.
  PAUSE_ALL_STARTED: 'pause_all:started',
  PAUSE_ALL_ENDED: 'pause_all:ended',
  PAUSE_ALL_SPOTLIGHT_PAUSED: 'pause_all:spotlight_paused',
  PAUSE_ALL_SPOTLIGHT_RESUMED: 'pause_all:spotlight_resumed',
  BILLING_SUPPLEMENTAL_INVOICE_SENT: 'billing:supplemental_invoice_sent',
  BILLING_SCHEDULED_INVOICE_SENT: 'billing:scheduled_invoice_sent',
  BILLING_CREDIT_ADDED: 'billing:credit_added',
  BILLING_CREDIT_CANCELLED: 'billing:credit_cancelled',
  BILLING_INVOICE_GROUPING_CHANGED: 'billing:invoice_grouping_changed',
  BILLING_INVOICE_EXEMPTION_CHANGED: 'billing:invoice_exemption_changed',
  BRAND_PAYMENT_METHOD_CHANGED: 'brand:payment_method_changed',
  BRAND_NET_TERMS_SET: 'brand:net_terms_set',
  BRAND_MANUAL_PAYMENT_TOGGLED: 'brand:manual_payment_toggled',
  BRAND_TRIAL_GRANTED: 'brand:trial_granted',
  BRAND_TRIAL_CANCELED: 'brand:trial_canceled',
  BRAND_TRIAL_EXTENDED: 'brand:trial_extended',
  BRAND_EVERGREEN_SPOTLIGHTS_GRANTED: 'brand:evergreen_spotlights_granted',
  BRAND_SPOTLIGHTS_TOGGLED: 'brand:spotlights_toggled',
  BRAND_NON_DTC_LINKS_TOGGLED: 'brand:non_dtc_links_toggled',
  BRAND_BELOW_MIN_BUDGET_TOGGLED: 'brand:below_min_budget_toggled',
  BRAND_FEATURE_FLAGS_SET: 'brand:feature_flags_set',
  BRAND_DAILY_BUDGET_UPDATED: 'brand:daily_budget_updated',
  CREATOR_VIP_TOGGLED: 'creator:vip_toggled',
  GROUP_CREATED: 'group:created',
  GROUP_UPDATED: 'group:updated',
  GROUP_BUDGET_SPLIT_UPDATED: 'group:budget_split_updated',
  GROUP_PAUSED: 'group:paused',
  GROUP_RESUMED: 'group:resumed',
  GROUP_ARCHIVED: 'group:archived',
  GROUP_RESTORED: 'group:restored',
  // A brand re-picked which sets a spotlight belongs to (before launch, or live: an added set launches
  // into, a removed one parks its ad).
  SPOTLIGHT_GROUPS_CHANGED: 'spotlight:groups_changed',
  // Maintenance re-pause of a member ad whose set is paused or whose membership is parked. Actor
  // 'system' — the hold outranks the spotlight's own live status.
  GROUP_PAUSE_ENFORCED: 'group:pause_enforced',
  // The nightly sweep confirmed the creator withdrew ShopMy's TikTok Business Center grant, so their
  // live TikTok ads were paused. Deliberately NOT in PAUSE_ACTIVITY_TYPES — nothing may lift this
  // pause except the creator re-authorizing.
  TIKTOK_GRANT_REVOKED: 'tiktok:grant_revoked',
  // The grant's term (set at link on TikTok's side) lapsed rather than being withdrawn — same pause,
  // but the fix is re-inviting the creator to renew. Also NOT in PAUSE_ACTIVITY_TYPES.
  TIKTOK_GRANT_EXPIRED: 'tiktok:grant_expired',
  // ShopMy released the grant itself (an admin unlink, or the creator's account deletion) — the
  // creator did nothing, and only a fresh authorization may lift this pause. NOT in
  // PAUSE_ACTIVITY_TYPES.
  TIKTOK_GRANT_RELEASED: 'tiktok:grant_released',
  // The creator disconnected their last TikTok account from ShopMy, so their live TikTok ads were
  // paused. NOT in PAUSE_ACTIVITY_TYPES — only a reconnect that re-grants may lift this pause.
  TIKTOK_ACCOUNT_DISCONNECTED: 'tiktok:account_disconnected',
  // The creator withdrew ShopMy-side push consent, so their live dark posts were paused. Nothing
  // writes this; it stays so recorded rows still render. Also NOT in PAUSE_ACTIVITY_TYPES.
  TIKTOK_PUSH_CONSENT_REVOKED: 'tiktok:push_consent_revoked',
  // An admin attested the push request's audio licensing — the interim stand-in for the audio
  // provenance classifier; the launch path refuses push ads whose request lacks this record.
  TIKTOK_PUSH_AUDIO_ATTESTED: 'tiktok:push_audio_attested',
  // An admin retired a TikTok-rejected ad and relaunched its replacement (the sync modal action).
  TIKTOK_AD_RELAUNCHED: 'tiktok:ad_relaunched',
  // The sync modal's per-strategy TikTok tools: push drifted fields, re-point or clear the
  // membership's ad, and pause a disconnected ad.
  TIKTOK_SYNCED: 'tiktok:synced',
  TIKTOK_AD_RECONNECTED: 'tiktok:ad_reconnected',
  TIKTOK_AD_DISCONNECTED: 'tiktok:ad_disconnected',
  TIKTOK_AD_PAUSED: 'tiktok:ad_paused'
};

// One entry per type: `action(meta)` is the verb phrase, optional `detail(meta)` is the trailing
// clause rendered after " — ". Both read only from the metadata bag so the copy stays a pure
// function of stored data.
const formatBudget = amount => (amount == null ? '(none)' : `$${amount}`);
const inStrategy = m => (m.strategyName ? ` in "${m.strategyName}"` : '');
const formatFeatureFlagChanges = flags =>
  Object.entries(flags || {})
    .map(([key, value]) => {
      const label = SPOTLIGHT_BRAND_FEATURE_FLAGS.find(flag => flag.key === key)?.label || key;
      return `${label} ${value === null ? 'cleared' : value ? 'on' : 'off'}`;
    })
    .join(', ');

const ACTIVITY_COPY = {
  [SPOTLIGHT_ACTIVITY_TYPES.AUTHORIZATION_GRANTED]: { action: m => `granted ${m.level}-level authorization` },
  [SPOTLIGHT_ACTIVITY_TYPES.AUTHORIZATION_REVOKED]: { action: m => `revoked ${m.level}-level authorization` },
  [SPOTLIGHT_ACTIVITY_TYPES.AUTH_REQUEST_SENT]: { action: m => `sent ${m.requestType} authorization request` },
  [SPOTLIGHT_ACTIVITY_TYPES.AUTH_REQUEST_ACCEPTED]: { action: m => `${m.auto ? 'auto-' : ''}accepted ${m.requestType} authorization request` },
  [SPOTLIGHT_ACTIVITY_TYPES.AUTH_REQUEST_REJECTED]: {
    action: m => `rejected ${m.requestType} authorization request`,
    detail: m => m.reason || null
  },
  [SPOTLIGHT_ACTIVITY_TYPES.LAUNCH_BLOCKED]: { action: () => 'launch attempt blocked', detail: m => m.reason || null },
  [SPOTLIGHT_ACTIVITY_TYPES.LAUNCH_ATTEMPTED]: {
    action: m => (m.count != null ? `attempted to launch ${m.count} spotlight${m.count === 1 ? '' : 's'}` : 'attempted to launch spotlight'),
    detail: m => (m.spotlightIds && m.spotlightIds.length ? `ids: ${m.spotlightIds.join(', ')}` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.LAUNCH_AUTO_AFTER_APPROVAL]: { action: () => 'auto-launching spotlight after approval' },
  [SPOTLIGHT_ACTIVITY_TYPES.SPOTLIGHT_PAUSED]: { action: () => 'paused spotlight', detail: m => (m.reason ? `Reason: ${m.reason}` : null) },
  [SPOTLIGHT_ACTIVITY_TYPES.SPOTLIGHT_AUTO_PAUSED]: {
    action: () => 'auto-paused spotlight',
    detail: () => 'return on ad spend fell below the pause guide'
  },
  // The stored type predates multi-platform; the copy follows metadata.platform (absent on
  // historical rows, which are all Meta).
  [SPOTLIGHT_ACTIVITY_TYPES.SPOTLIGHT_AUTO_PAUSED_TO_MATCH_META]: {
    action: m => `auto-paused in ShopMy to match ${getGroupPlatformSpec(m.platform).displayName}`,
    detail: m => `its ad was paused on ${getGroupPlatformSpec(m.platform).displayName} directly`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.META_REPAUSED_TO_MATCH_SHOPMY]: {
    action: () => 're-paused the ad on Meta to match ShopMy',
    detail: m => `was paused in ShopMy but its ad was still ${m.metaStatus || 'active'} on Meta`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.META_OFFPHASE_AD_REPAUSED]: {
    action: m => `re-paused the off-phase ${m.phase} ad on Meta`,
    detail: m => (m.currentPhase ? `spotlight is now in ${m.currentPhase} phase` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.SPOTLIGHT_RESUMED]: { action: () => 'resumed spotlight', detail: m => (m.status ? `now ${m.status}` : null) },
  [SPOTLIGHT_ACTIVITY_TYPES.SPOTLIGHT_VALIDATED]: { action: () => 'manually validated spotlight' },
  [SPOTLIGHT_ACTIVITY_TYPES.SPOTLIGHT_DEMOTED]: { action: () => 'manually demoted spotlight' },
  [SPOTLIGHT_ACTIVITY_TYPES.SPOTLIGHT_CLOSED]: { action: () => 'closed spotlight', detail: m => (m.priorStatus ? `was ${m.priorStatus}` : null) },
  [SPOTLIGHT_ACTIVITY_TYPES.DETAILS_UPDATED]: {
    action: () => 'updated spotlight details',
    detail: m => (m.fields && m.fields.length ? m.fields.join('; ') : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BUDGET_UPDATED]: { action: () => 'updated daily budget', detail: m => `${formatBudget(m.from)} → ${formatBudget(m.to)}` },
  [SPOTLIGHT_ACTIVITY_TYPES.AUDIENCE_UPDATED]: {
    action: () => 'updated brand audience targeting (via spotlight)',
    detail: m => (m.fields && m.fields.length ? `fields: ${m.fields.join(', ')}` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.SETTINGS_UPDATED]: {
    action: () => 'updated brand spotlight settings',
    detail: m => (m.fields && m.fields.length ? `fields: ${m.fields.join(', ')}` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.EXCLUSIONS_UPDATED]: {
    action: () => 'updated custom-audience exclusions',
    detail: m => `experiment: ${m.experimentCount}, validated: ${m.validatedCount}`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.EXCLUSIONS_ADDED]: {
    action: () => 'excluded custom audience (self-serve)',
    detail: m => `audience ${m.audienceId}; scope: ${m.scope}`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.EXCLUSIONS_SET]: {
    action: () => 'excluded custom audience(s)',
    detail: m => `${m.count} audience(s); scope: ${m.scope}`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.META_SYNCED]: {
    action: m => (m.success ? 'synced spotlight edits to its live ads' : 'synced spotlight edits to its live ads (partial failure)'),
    detail: m => (m.errorFields && m.errorFields.length ? `errors: ${m.errorFields.join(', ')}` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.META_CREATIVE_REBUILT]: {
    action: m => (m.success ? 'rebuilt Meta creative' : 'attempted to rebuild Meta creative (failed)'),
    detail: m => m.error || (m.creativeId ? `new creative ${m.creativeId}` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.IDENTITY_REVERIFY_REGRESSED]: {
    action: () => 'identity regressed after launch — rebuilt the creative to restore it',
    detail: m => (m.status ? `ad ${m.adId} was ${m.status}` : m.adId ? `ad ${m.adId}` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.META_AD_RECONNECTED]: {
    action: m => `manually reconnected ${m.phase} Meta ad`,
    detail: m => `${m.priorAdId || '(none)'} → ${m.newAdId}${m.newAdName ? ` (${m.newAdName})` : ''}`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.META_AD_RELAUNCHED]: {
    action: m => (m.success ? 'relaunched Meta ad' : 'attempted to relaunch Meta ad (failed)'),
    detail: m => {
      const retired = m.retiredAdId ? ` (retired ${m.retiredAdId})` : '';
      if (m.error) return `${m.error}${retired}`;
      return m.adId ? `new ad ${m.adId}${retired}` : null;
    }
  },
  [SPOTLIGHT_ACTIVITY_TYPES.META_AD_DISCONNECTED]: {
    action: m => `disconnected ${m.phase} Meta ad`,
    detail: m => `${m.priorAdId} → (none)`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.META_AD_PAUSED]: { action: () => 'paused disconnected Meta ad', detail: m => `paused ad ${m.adId}` },
  [SPOTLIGHT_ACTIVITY_TYPES.META_ADSET_DISCONNECTED]: {
    action: m => `disconnected ${m.phase} Meta ad set`,
    detail: m => `ad set ${m.priorAdSetId || '(none)'} + ad ${m.priorAdId || '(none)'} → (none)`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.META_PIXEL_CONNECTED]: { action: () => 'connected the Meta pixel', detail: m => `pixel ${m.pixelId}` },
  [SPOTLIGHT_ACTIVITY_TYPES.MEDIA_TRIM_FAILED]: {
    action: () => 'video trim/mute failed — uploaded the original instead',
    detail: m => m.error || null
  },
  [SPOTLIGHT_ACTIVITY_TYPES.TRIAGE_MARKED_ERRORED]: {
    action: () => 'marked the spotlight errored (triage)',
    detail: m => `${m.errorType}${m.reason ? ` — ${m.reason}` : ''}`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.TRIAGE_RESOLVED]: {
    action: () => 'resolved the spotlight triage error',
    detail: m => (m.resumed ? 'resumed to its prior live status' : 'returned to the pipeline')
  },
  [SPOTLIGHT_ACTIVITY_TYPES.KILLSWITCH_DISABLED]: {
    action: () => 'disabled Spotlights for the creator (kill switch)',
    detail: m => `paused ${m.pausedCount}, canceled ${m.canceledCount}, withdrew ${m.withdrawnRequests} request(s)${m.reason ? ` — ${m.reason}` : ''}`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.KILLSWITCH_ENABLED]: {
    action: () => 're-enabled Spotlights for the creator (kill switch)',
    detail: m => `resumed ${m.resumedCount} spotlight(s)`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BILLING_AUTO_PAUSED]: {
    action: () => 'auto-paused spotlight for unpaid ad spend',
    detail: m => `${m.attemptCount} of ${m.maxAttempts} payment attempts failed on invoice ${m.invoiceId}`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BILLING_AUTO_RESUMED]: {
    action: () => 'resumed spotlight after the ad-spend invoice was paid',
    detail: m => (m.status ? `now ${m.status}` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BILLING_ADMIN_RESUMED]: {
    action: () => 'resumed billing-paused spotlight before the ad-spend invoice was settled',
    detail: m => (m.status ? `now ${m.status}` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.PAUSE_ALL_STARTED]: {
    action: m => (m.retry ? 'retried Pause all Spotlights' : 'paused all Spotlights'),
    detail: m =>
      `paused ${m.pausedCount} of ${m.pausableCount} spotlight(s)${
        m.withdrawnRequests ? `, cancelled ${m.withdrawnRequests} pending request(s)` : ''
      }` +
      `${m.failedCount ? `, ${m.failedCount} could not be paused` : ''}${m.remainingCount ? `, ${m.remainingCount} not yet reached` : ''}` +
      `${m.budgetZeroed ? ' — daily budget set to $0' : ''}`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.PAUSE_ALL_ENDED]: {
    action: () => 'resumed Spotlights after Pause all',
    detail: m =>
      `resumed ${m.resumedCount} spotlight(s)${m.unresumedCount ? `, ${m.unresumedCount} left paused` : ''}` +
      `${m.heldCount ? `, ${m.heldCount} held by another block` : ''}${m.rescheduledCount ? `, re-queued ${m.rescheduledCount} launch(es)` : ''}` +
      `${m.remainingCount ? `, ${m.remainingCount} not yet reached` : ''}${m.adsSpendLimit != null ? ` at $${m.adsSpendLimit}/day` : ''}`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.PAUSE_ALL_SPOTLIGHT_PAUSED]: {
    action: () => 'paused spotlight via Pause all Spotlights',
    detail: m => (m.restoreStatus ? `was ${m.restoreStatus}` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.PAUSE_ALL_SPOTLIGHT_RESUMED]: {
    action: () => 'resumed spotlight via Resume Spotlights',
    detail: m => (m.status ? `now ${m.status}` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BILLING_SUPPLEMENTAL_INVOICE_SENT]: {
    action: () => 'sent a supplemental ad-spend invoice',
    detail: m => `invoice ${m.invoiceId} for $${m.amount} (${m.lineItemCount} spotlight(s), ${m.periodStart} – ${m.periodEnd})`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BILLING_SCHEDULED_INVOICE_SENT]: {
    action: () => 'generated the scheduled invoice ahead of the nightly run',
    detail: m =>
      `invoice ${m.invoiceId} for $${m.amount}${m.groupName ? ` (set ${m.groupName})` : ''} (${m.periodStart} – ${m.periodEnd})` +
      (m.creditAmount ? ` — $${m.creditAmount} credit applied` : '') +
      (m.reconciliationOverridden
        ? ` — reconciliation overridden (${
            [m.metaUnavailable && 'Meta unavailable', m.tiktokUnavailable && 'TikTok unavailable', m.metaSpend != null && `$${m.metaSpend} on Meta`]
              .filter(Boolean)
              .join(', ') || 'platform spend unverified'
          }): ${m.overrideReason}`
        : '')
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BILLING_CREDIT_ADDED]: {
    action: m => `added a $${m.amount} credit toward upcoming ad-spend invoices`,
    detail: m => m.description || null
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BILLING_CREDIT_CANCELLED]: {
    action: m => `cancelled ad-spend invoice credit #${m.creditId}`,
    detail: m => `$${m.remaining} of $${m.amount} was still unapplied — ${m.description}`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BILLING_INVOICE_GROUPING_CHANGED]: {
    action: m =>
      m.grouping === 'per_group' ? 'switched ad-spend billing to one invoice per set' : 'switched ad-spend billing to a single combined invoice'
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BILLING_INVOICE_EXEMPTION_CHANGED]: {
    action: m => (m.exempt ? 'disabled new customer ad-spend invoices' : `resumed customer ad-spend invoicing for spend from ${m.startDate}`),
    detail: m => m.reason || null
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BRAND_PAYMENT_METHOD_CHANGED]: {
    action: () => 'changed the Spotlights payment method',
    detail: m => `→ ${m.paymentMethodId}`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BRAND_NET_TERMS_SET]: {
    action: m => (m.days > 0 ? `set Spotlights net terms to ${m.days} day(s)` : 'removed Spotlights net terms'),
    detail: m => `${m.previousDays}d → ${m.days}d`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BRAND_MANUAL_PAYMENT_TOGGLED]: {
    action: m => (m.enabled ? 'enabled Spotlights manual payment (skip payment method)' : 'disabled Spotlights manual payment')
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BRAND_TRIAL_GRANTED]: {
    action: m => `granted a ${m.days}-day Spotlights trial`,
    detail: m => (m.brandIds && m.brandIds.length ? `Brand IDs: ${m.brandIds.join(', ')}` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BRAND_TRIAL_CANCELED]: { action: () => 'canceled the Spotlights trial' },
  [SPOTLIGHT_ACTIVITY_TYPES.BRAND_TRIAL_EXTENDED]: { action: m => `extended the Spotlights trial to ${m.days} days` },
  [SPOTLIGHT_ACTIVITY_TYPES.BRAND_EVERGREEN_SPOTLIGHTS_GRANTED]: {
    action: () => 'granted evergreen Spotlights (included in package, $0)',
    detail: m => (m.brandIds && m.brandIds.length ? `Brand IDs: ${m.brandIds.join(', ')}` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BRAND_SPOTLIGHTS_TOGGLED]: { action: m => `${m.enabled ? 'enabled' : 'disabled'} Spotlights for the brand` },
  [SPOTLIGHT_ACTIVITY_TYPES.BRAND_NON_DTC_LINKS_TOGGLED]: {
    action: m => `${m.enabled ? 'enabled' : 'disabled'} non-DTC destination links for the brand`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BRAND_BELOW_MIN_BUDGET_TOGGLED]: {
    action: m => `${m.enabled ? 'enabled' : 'disabled'} the below-minimum daily budget override for the brand`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BRAND_FEATURE_FLAGS_SET]: {
    action: () => 'set Spotlights feature flags',
    detail: m => {
      const brandList = m.brandIds && m.brandIds.length ? `Brand IDs: ${m.brandIds.join(', ')}` : null;
      return [formatFeatureFlagChanges(m.flags), brandList].filter(Boolean).join('; ') || null;
    }
  },
  [SPOTLIGHT_ACTIVITY_TYPES.BRAND_DAILY_BUDGET_UPDATED]: {
    action: () => 'updated the brand daily budget',
    detail: m => `${formatBudget(m.from)} → ${formatBudget(m.to)}`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.CREATOR_VIP_TOGGLED]: {
    action: m => `${m.granted ? 'granted' : 'removed'} Spotlight VIP`,
    detail: m => (m.userIds && m.userIds.length ? `User IDs: ${m.userIds.join(', ')}` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.GROUP_CREATED]: { action: m => `created spotlight group "${m.name}"` },
  [SPOTLIGHT_ACTIVITY_TYPES.GROUP_UPDATED]: {
    action: m => `updated spotlight group "${m.name}"`,
    detail: m => (m.changedKeys && m.changedKeys.length ? `fields: ${m.changedKeys.join(', ')}` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.GROUP_BUDGET_SPLIT_UPDATED]: {
    action: () => 'updated the group budgets',
    // Rows written before the fixed-dollar cutover carry percent shares; both shapes stay renderable.
    detail: m =>
      m.splits && m.splits.length
        ? m.splits
            .map(s => (s.dailyBudget != null ? `${s.name}: $${Number(s.dailyBudget).toFixed(2)}/day` : `${s.name}: ${s.budgetPercentage}%`))
            .join(', ')
        : null
  },
  [SPOTLIGHT_ACTIVITY_TYPES.GROUP_PAUSED]: { action: m => `paused spotlight group "${m.name}"` },
  [SPOTLIGHT_ACTIVITY_TYPES.GROUP_RESUMED]: { action: m => `resumed spotlight group "${m.name}"` },
  [SPOTLIGHT_ACTIVITY_TYPES.GROUP_ARCHIVED]: { action: m => `archived spotlight group "${m.name}"` },
  [SPOTLIGHT_ACTIVITY_TYPES.GROUP_RESTORED]: { action: m => `restored spotlight group "${m.name}"` },
  [SPOTLIGHT_ACTIVITY_TYPES.SPOTLIGHT_GROUPS_CHANGED]: {
    action: m => `set the spotlight's groups to ${(m.groupNames || []).map(name => `"${name}"`).join(', ') || (m.groupIds || []).join(', ')}`,
    detail: m => (m.parked ? `${m.parked} ad${m.parked === 1 ? '' : 's'} paused in the removed set${m.parked === 1 ? '' : 's'}` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.GROUP_PAUSE_ENFORCED]: {
    action: m => (m.heldBy === 'membership' ? `re-paused the ad in group "${m.name}" the spotlight was moved out of` : `re-paused ad for paused group "${m.name}"`),
    detail: m => (m.metaStatus ? `Meta still had it ${m.metaStatus}` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.TIKTOK_GRANT_REVOKED]: {
    action: () => 'paused the TikTok ad',
    detail: m => `the creator${m.handle ? ` (@${m.handle})` : ''} revoked ShopMy's TikTok ads authorization`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.TIKTOK_GRANT_EXPIRED]: {
    action: () => 'paused the TikTok ad',
    detail: m => `the creator${m.handle ? ` (@${m.handle})` : ''}'s TikTok ads authorization term ended — re-invite them to renew`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.TIKTOK_GRANT_RELEASED]: {
    action: () => 'paused the TikTok ad',
    detail: m =>
      `ShopMy released the creator's TikTok ads authorization${m.releaseSource ? ` (${m.releaseSource})` : ''} — re-invite them to relaunch`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.TIKTOK_ACCOUNT_DISCONNECTED]: {
    action: () => 'paused the TikTok ad',
    detail: m => `the creator${m.handle ? ` (@${m.handle})` : ''} disconnected their TikTok account from ShopMy`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.TIKTOK_PUSH_CONSENT_REVOKED]: {
    action: () => 'paused the TikTok push ad',
    detail: () => 'the creator withdrew consent for ad-only posts under their handle'
  },
  [SPOTLIGHT_ACTIVITY_TYPES.TIKTOK_PUSH_AUDIO_ATTESTED]: {
    action: () => 'attested the push request’s audio licensing',
    detail: m => (m.requestId ? `request ${m.requestId}` : null)
  },
  [SPOTLIGHT_ACTIVITY_TYPES.TIKTOK_AD_RELAUNCHED]: {
    action: m => (m.success ? 'relaunched TikTok ad' : 'attempted to relaunch TikTok ad (failed)'),
    detail: m => {
      const retired = m.retiredAdId ? ` (retired ${m.retiredAdId})` : '';
      if (m.error) return `${m.error}${retired}`;
      return m.adId ? `new ad ${m.adId}${retired}` : null;
    }
  },
  [SPOTLIGHT_ACTIVITY_TYPES.TIKTOK_SYNCED]: {
    action: m => `pushed ShopMy's values to the TikTok ad${inStrategy(m)}${m.success ? '' : ' (partial failure)'}`,
    detail: m => {
      const pushed = m.fields && m.fields.length ? `pushed ${m.fields.join(', ')}` : m.success ? 'nothing had drifted' : null;
      const errors = m.errorFields && m.errorFields.length ? `errors: ${m.errorFields.join(', ')}` : m.error || null;
      return [pushed, errors].filter(Boolean).join('; ') || null;
    }
  },
  [SPOTLIGHT_ACTIVITY_TYPES.TIKTOK_AD_RECONNECTED]: {
    action: m => `manually reconnected the TikTok ad${inStrategy(m)}`,
    detail: m => `${m.priorAdId || '(none)'} → ${m.newAdId}${m.newAdName ? ` (${m.newAdName})` : ''}`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.TIKTOK_AD_DISCONNECTED]: {
    action: m => `disconnected the TikTok ad${inStrategy(m)}`,
    detail: m => `${m.priorAdId} → (none)`
  },
  [SPOTLIGHT_ACTIVITY_TYPES.TIKTOK_AD_PAUSED]: { action: m => `paused a TikTok ad${inStrategy(m)}`, detail: m => `ad ${m.adId}` }
};

// "Admin Jane ", "Admin Jane on behalf of Brand ", "Creator ", "" — mirrors who-did-it attribution,
// including the simulate-as case where an admin acted as a brand/creator.
const activityActorPrefix = ({ actor, actingUserLabel }) => {
  if (actingUserLabel && actor === 'admin') return `Admin ${actingUserLabel} `;
  if (actingUserLabel && (actor === 'brand' || actor === 'creator')) {
    return `Admin ${actingUserLabel} on behalf of ${actor === 'brand' ? 'Brand' : 'Creator'} `;
  }
  if (actor === 'brand') return 'Brand ';
  if (actor === 'creator') return 'Creator ';
  if (actor === 'admin') return 'Admin ';
  return '';
};

/**
 * Render one activity row (or notifySpotlightActivity input) into its actor-prefixed phrase, e.g.
 * "Admin Jane paused spotlight — Reason: off-brand". No Slack markup, no spotlight subject — callers
 * add their own framing.
 *
 * @param {object} activity
 * @param {string}  activity.type              a SPOTLIGHT_ACTIVITY_TYPES value
 * @param {'brand'|'creator'|'admin'|'system'} [activity.actor]
 * @param {string} [activity.actingUserLabel]  admin name, for the "Admin X" / "on behalf of" prefix
 * @param {object} [activity.metadata]         params the copy reads
 * @returns {string}
 */
const formatSpotlightActivity = ({ type, actor, actingUserLabel, metadata } = {}) => {
  const meta = metadata || {};
  const copy = ACTIVITY_COPY[type];
  const action = copy ? copy.action(meta) : meta.action || type;
  const detail = copy && copy.detail ? copy.detail(meta) : meta.details || null;
  return `${activityActorPrefix({ actor, actingUserLabel })}${action}${detail ? ` — ${detail}` : ''}`;
};

// Activity types that leave a spotlight paused. Kill switch + triage are here so "most recent pause"
// detection is accurate, but note they have their own resume gating (kill-switch enable / triage
// resolve) — the sync auto-unpause path should only lift an automation pause it owns.
const PAUSE_ACTIVITY_TYPES = [
  SPOTLIGHT_ACTIVITY_TYPES.SPOTLIGHT_PAUSED,
  SPOTLIGHT_ACTIVITY_TYPES.PAUSE_ALL_SPOTLIGHT_PAUSED,
  SPOTLIGHT_ACTIVITY_TYPES.SPOTLIGHT_AUTO_PAUSED_TO_MATCH_META,
  SPOTLIGHT_ACTIVITY_TYPES.KILLSWITCH_DISABLED,
  SPOTLIGHT_ACTIVITY_TYPES.TRIAGE_MARKED_ERRORED,
  SPOTLIGHT_ACTIVITY_TYPES.BILLING_AUTO_PAUSED
];

// A pause is "explicit" only when the brand or creator paused it themselves (spotlight:paused by
// that actor, or the brand's Pause all, including an admin acting on their behalf via simulate-as).
// Every other pause — sync reconciliation, kill switch, triage — is automation/admin. This is the
// signal the auto-unpause reconciliation keys on: explicit → leave paused; automation → eligible
// to unpause.
const EXPLICIT_PAUSE_ACTIVITY_TYPES = [SPOTLIGHT_ACTIVITY_TYPES.SPOTLIGHT_PAUSED, SPOTLIGHT_ACTIVITY_TYPES.PAUSE_ALL_SPOTLIGHT_PAUSED];
const isExplicitPause = entry =>
  !!entry && EXPLICIT_PAUSE_ACTIVITY_TYPES.includes(entry.type) && (entry.actor === 'brand' || entry.actor === 'creator');

/**
 * The most-recent pause in a spotlight's activity, with its provenance. Feeds the modal's pause
 * banner and shared with the auto-unpause reconciliation so both agree on who paused last.
 *
 * @param {Array<{ type: string, actor?: string, createdAt?: string }>} activityLog
 * @returns {{ entry: object, isExplicit: boolean } | null} null when never paused
 */
const summarizeLastPause = (activityLog = []) => {
  const entry = (activityLog || [])
    .filter(e => e && PAUSE_ACTIVITY_TYPES.includes(e.type))
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  if (!entry) return null;
  return { entry, isExplicit: isExplicitPause(entry) };
};

module.exports = {
  SPOTLIGHT_ACTIVITY_TYPES,
  formatSpotlightActivity,
  PAUSE_ACTIVITY_TYPES,
  isExplicitPause,
  summarizeLastPause
};
